if (!customElements.get('catalogue-browser')) {
  customElements.define(
    'catalogue-browser',
    class CatalogueBrowser extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.content = this.querySelector('[data-catalogue-content]');
        this.live = this.querySelector('[data-catalogue-live]');
        if (!this.sectionId || !this.content) return;
        this.filterQueue = Promise.resolve();
        this.navigationGeneration = (this.navigationGeneration || 0) + 1;

        this.positions = new Map();
        try {
          this.positions = new Map(JSON.parse(sessionStorage.getItem('pidoof:catalogue-positions') || '[]'));
        } catch {
          // History still works in this document when storage is unavailable.
        }
        this.historyKey = window.history.state?.pidoofCatalogue || crypto.randomUUID();
        window.history.replaceState({ ...window.history.state, pidoofCatalogue: this.historyKey }, '');
        this.rememberPosition();

        this.onClick = (event) => {
          const link = event.target.closest('a[data-catalogue-link]');
          if (!link || !this.contains(link) || event.defaultPrevented || event.button !== 0 ||
            event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          if (link.origin !== window.location.origin) return;

          event.preventDefault();
          if (link.dataset.filterLink !== undefined) {
            const generation = this.navigationGeneration;
            const focusKey = link.dataset.focusKey;
            const keepDrawerOpen = !!link.closest('.filter-drawer[open]');
            const selector = `[data-focus-key="${CSS.escape(focusKey)}"]`;
            // Each next choice uses freshly rendered URLs. Otherwise a quick
            // second click can discard the first facet via a stale href.
            this.filterQueue = this.filterQueue.then(() => {
              if (!this.isConnected || generation !== this.navigationGeneration) return;
              const currentLink = this.querySelector(selector);
              if (currentLink) return this.load(currentLink.href, { keepDrawerOpen, focusKey, queuedFilter: true });
            });
          } else {
            this.filterQueue = this.load(link.href);
          }
        };

        this.onSubmit = (event) => {
          const form = event.target.closest('form[data-sort]');
          if (!form || !this.contains(form)) return;

          event.preventDefault();
          const url = new URL(form.action || window.location.href, window.location.origin);
          url.search = new URLSearchParams(new FormData(form)).toString();
          this.filterQueue = this.load(url.toString());
        };

        this.onChange = (event) => {
          const select = event.target.closest('[data-sort] select');
          if (select && this.contains(select)) {
            // Release the native phone picker before replacing its select.
            select.blur();
            select.form?.requestSubmit();
          }
        };

        this.onPopState = (event) => {
          const position = this.positions.get(event.state?.pidoofCatalogue);
          if (position) {
            this.scrollRestoration ??= window.history.scrollRestoration;
            window.history.scrollRestoration = 'manual';
          }
          this.filterQueue = this.load(window.location.href, { push: false, historyKey: event.state?.pidoofCatalogue, position });
        };
        this.onScroll = () => {
          if (!this.controller) this.rememberPosition();
        };
        this.onPageHide = () => {
          this.rememberPosition();
          try {
            // Keep recent positions across refreshes and product-page visits.
            sessionStorage.setItem('pidoof:catalogue-positions', JSON.stringify([...this.positions].slice(-200)));
          } catch {
            // Browser storage is optional, including in private browsing.
          }
          this.restoreScrollMode();
        };
        this.onOutsideClick = (event) => {
          const drawer = this.querySelector('.filter-drawer[open]');
          if (drawer && !drawer.contains(event.target)) this.closeDrawer();
        };
        this.onKeyDown = (event) => {
          if (event.key !== 'Escape' || !this.querySelector('.filter-drawer[open]')) return;
          event.preventDefault();
          this.closeDrawer();
          this.querySelector('.filter-drawer__summary')?.focus({ preventScroll: true });
        };

        this.addEventListener('click', this.onClick);
        this.addEventListener('submit', this.onSubmit);
        this.addEventListener('change', this.onChange);
        this.addEventListener('keydown', this.onKeyDown);
        document.addEventListener('click', this.onOutsideClick);
        window.addEventListener('popstate', this.onPopState);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('pagehide', this.onPageHide);
        this.dataset.enhanced = '';
      }

      disconnectedCallback() {
        this.navigationGeneration++;
        this.controller?.abort();
        this.controller = null;
        this.restoreScrollMode();
        this.removeAttribute('aria-busy');
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('submit', this.onSubmit);
        this.removeEventListener('change', this.onChange);
        this.removeEventListener('keydown', this.onKeyDown);
        document.removeEventListener('click', this.onOutsideClick);
        window.removeEventListener('popstate', this.onPopState);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('pagehide', this.onPageHide);
      }

      closeDrawer() {
        this.querySelector('.filter-drawer[open]')?.removeAttribute('open');
      }

      rememberPosition() {
        if (this.historyKey) this.positions.set(this.historyKey, { left: window.scrollX, top: window.scrollY });
      }

      restoreScrollMode() {
        if (this.scrollRestoration === undefined) return;
        window.history.scrollRestoration = this.scrollRestoration;
        this.scrollRestoration = undefined;
      }

      async load(href, { push = true, historyKey, position, keepDrawerOpen = false, focusKey, queuedFilter = false } = {}) {
        if (!queuedFilter) this.navigationGeneration++;
        if (push) this.rememberPosition();
        // Set this before creating history entries, not during popstate: by
        // then a browser may already have scheduled its own smooth restore.
        this.scrollRestoration ??= window.history.scrollRestoration;
        window.history.scrollRestoration = 'manual';
        this.controller?.abort();
        const controller = new AbortController(); this.controller = controller;

        const destination = new URL(href, window.location.origin);
        if (this.dataset.defaultSort && !destination.searchParams.get('sort_by')) {
          destination.searchParams.set('sort_by', this.dataset.defaultSort);
        }
        const request = new URL(destination);
        request.searchParams.set('section_id', this.sectionId);
        const drawer = keepDrawerOpen ? this.querySelector('.filter-drawer[open]') : null;
        if (!drawer) this.closeDrawer();

        this.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(request, { signal: controller.signal });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const markup = await response.text();
          if (this.controller !== controller) return;
          const parsed = new DOMParser().parseFromString(markup, 'text/html');
          const nextContent = parsed.querySelector('[data-catalogue-content]');
          const nextBrowser = parsed.querySelector('[data-catalogue]');
          if (!nextContent) throw new Error('Catalogue section markup missing');

          // Save the outgoing position before a shorter grid can clamp it.
          if (push) this.rememberPosition();
          // Read the current state, so an outside click or Escape during the
          // request stays dismissed when the response arrives.
          const drawerStillOpen = !!drawer?.open;
          const drawerPosition = { left: window.scrollX, top: window.scrollY };
          const panelScroll = drawer?.querySelector('.filter-drawer__panel')?.scrollTop || 0;
          this.content.innerHTML = nextContent.innerHTML;
          const nextDrawer = this.querySelector('.filter-drawer');
          if (drawerStillOpen && nextDrawer) {
            nextDrawer.open = true;
            const panel = nextDrawer.querySelector('.filter-drawer__panel');
            if (panel) panel.scrollTop = panelScroll;
          }
          this.historyKey = push ? crypto.randomUUID() : historyKey;
          if (push) window.history.pushState({ pidoofCatalogue: this.historyKey }, '', destination);
          this.updateMetadata(nextBrowser);

          const resultCount = this.querySelector('[data-result-count]')?.textContent.trim();
          if (this.live && resultCount)
            this.live.textContent = (this.dataset.updatedTemplate || '[[count]]').replace('[[count]]', resultCount);

          if (push && drawerStillOpen && nextDrawer) {
            const selectedFilter = focusKey
              ? nextDrawer.querySelector(`[data-focus-key="${CSS.escape(focusKey)}"]`)
              : null;
            (selectedFilter || nextDrawer.querySelector('summary'))?.focus({ preventScroll: true });
            window.scrollTo({ ...drawerPosition, behavior: 'instant' });
          } else if (push && queuedFilter && !drawer && focusKey && this.querySelector(`[data-focus-key="${CSS.escape(focusKey)}"]`)) {
            // Desktop facets are multi-select too. Keep the shopper in the
            // rail while successive selections resolve in order.
            this.querySelector(`[data-focus-key="${CSS.escape(focusKey)}"]`).focus({ preventScroll: true });
            window.scrollTo({ ...drawerPosition, behavior: 'instant' });
          } else if (push) {
            // Focus the refreshed results, never a replacement native picker or
            // a link hidden inside the now-closed drawer. Pagination, filters
            // and sorting all start at the top of the updated catalogue.
            this.querySelector('[data-results-focus]')?.focus({ preventScroll: true });
            this.scrollIntoView({ block: 'start', behavior: 'instant' });
          } else if (position) {
            // Native history restores before the asynchronous grid exists and
            // can clamp to the shorter page. Restore again after rendering.
            window.scrollTo({ ...position, behavior: 'instant' });
          }
          this.rememberPosition();

          this.dispatchEvent(new CustomEvent('catalogue:updated', { bubbles: true }));
        } catch (error) {
          if (error.name === 'AbortError' || this.controller !== controller) return;
          this.navigationGeneration++;
          window.location.assign(destination);
        } finally {
          if (this.controller !== controller) return;
          this.controller = null;
          this.removeAttribute('aria-busy');
        }
      }

      updateMetadata(source) {
        if (!source) return;

        const pageTitle = source.dataset.pageTitle?.trim();
        const socialTitle = source.dataset.socialTitle?.trim();
        const description = source.dataset.description?.trim();
        const canonicalUrl = source.dataset.canonicalUrl?.trim();
        const robotsValue = source.dataset.robots?.trim() || '';

        if (pageTitle) {
          document.title = pageTitle;
        }

        if (socialTitle) {
          for (const selector of ['meta[property="og:title"]', 'meta[name="twitter:title"]']) {
            document.querySelector(selector)?.setAttribute('content', socialTitle);
          }
        }

        if (description) {
          for (const selector of [
            'meta[name="description"]',
            'meta[property="og:description"]',
            'meta[name="twitter:description"]',
          ]) {
            document.querySelector(selector)?.setAttribute('content', description);
          }
        }

        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical && canonicalUrl) {
          canonical.href = canonicalUrl;
          document.querySelector('meta[property="og:url"]')?.setAttribute('content', canonicalUrl);
        }

        let robots = document.querySelector('meta[name="robots"]');
        if (robotsValue) {
          if (!robots) {
            robots = document.createElement('meta');
            robots.name = 'robots';
            document.head.append(robots);
          }
          robots.content = robotsValue;
        } else {
          robots?.remove();
        }
      }
    }
  );
}
